import React from 'react';
import { Box, Grid, Stack, Typography } from '@mui/material';
import _ from 'lodash';
import i18n from '../../i18n';
import { apiManage_rbp } from '../../request';
import { FirstMenu } from '../../models/first-menu.entity';
import GlobalNotification from '../../component/common/snackbar';
import setLoading from '../../component/linear-determinate';
import CommonInput from '../../component/form/common-input.component';
import { IOSSwitch } from '../../component/switch/IOSSwitch';
import { ReactComponent as ErrorRoundedIcon } from '../../static/images/svg/material-symbols_error-rounded.svg';
import { RbpCloudSubFunctionAuxiliaryModeContext } from './rbp-cloud-sub-function-auxiliary-mode-context';
import SubFunctionProjectSettingSaveButton from './sub-function-project-setting-save-button.component';
import FirstLevelMenuTagPicker from './first-level-menu-tag-picker.component';
import { SubFunctionAuxiliaryHint } from './sub-function-auxiliary-hint.component';
import { KWA_CORE_SETTING_KEYS, KWA_RUNTIME_SETTING_KEYS } from './kwa-setting-constants';
import { KwaSettingPageSkeleton } from './sub-function-settings-skeletons.component';
import './scss/kwa-setting.page.scss';

/** 辅助模式下各设置 id 对应 Figma 次要文案 i18n key（node 1861:53430，稿中重复句按稿保留） */
const KWA_AUX_HINT_I18N_KEYS: Partial<Record<number, string>> = {
    17: 'figma_aux_sold_out_disabled_in_menu',
    16: 'figma_aux_sold_out_disabled_in_menu',
    44: 'figma_aux_sold_out_disabled_in_menu',
    61: 'figma_aux_kitchen_sync_cross_terminal',
    127: 'figma_aux_sold_out_disabled_in_menu',
    128: 'figma_aux_sold_out_disabled_in_menu',
    76: 'figma_aux_sold_out_disabled_in_menu',
    104: 'figma_aux_kitchen_sync_cross_terminal',
};

/**
 * 单行配置定义：与 `getSettingById` / `postDishWeigthForKWA` 的读写形态一致。
 */
type KwaSettingRow = {
    /** 设置项 id（41 为菜单多选，不在本数组中） */
    key: number;
    /** 展示形态 */
    type: 'switch' | 'input';
    /** 当前值 */
    value: boolean | string | number;
    /** 文案 i18n key */
    labelKey: string;
    /** 数字输入时传入 BaseInput 的 type */
    inputType?: 'number';
};

type Props = Record<string, never>;

type State = {
    /** 全页保存中，用于禁用保存按钮 */
    isSaving: boolean;
    /** 一级菜单列表，用于「不参与轮次推进」多选 */
    menuData: FirstMenu[];
    /** 已选一级菜单 id（设置 id 41） */
    curSelectIds: number[];
    /** KWA 相关开关与数值配置行 */
    settingArr: KwaSettingRow[];
    /** 首包拉取完成前展示骨架 */
    initialPageDataReady: boolean;
};

/**
 * 构建初始 `settingArr`，默认值与 RBP2 KWA 页一致。
 * @returns 初始配置行列表
 */
function buildInitialSettingRows(): KwaSettingRow[] {
    return [
        { key: 17, type: 'switch', value: false, labelKey: 'kwa_setting_page_order_restaurant_mode' },
        { key: 16, type: 'switch', value: false, labelKey: 'kwa_setting_page_rounds_to_promote' },
        { key: 44, type: 'switch', value: false, labelKey: 'kwa_setting_page_full_page_sound_notification' },
        { key: 61, type: 'switch', value: false, labelKey: 'kwa_setting_page_magnetic_link_mode' },
        { key: 127, type: 'switch', value: false, labelKey: 'kwa_setting_page_overtime_auto_rush' },
        { key: 128, type: 'input', value: '', labelKey: 'kwa_setting_page_ovetime_setting', inputType: 'number' },
        { key: 76, type: 'input', value: '', labelKey: 'kwa_setting_page_turn_weight_value', inputType: 'number' },
        { key: 104, type: 'input', value: '3', labelKey: 'kwa_setting_page_refresh_cycle', inputType: 'number' },
    ];
}

/**
 * 云 RBP「子功能管理」— KWA 设置：菜单排除轮次、核心开关与运行参数；接口走 `apiManage_rbp`（与 RBP2 语义一致）。
 * 布局对齐 Figma node 1871:53811 内容区。隧道校验在子功能壳页 `HookDecorator` 中统一完成。
 */
class KwaSettingPage extends React.Component<Props, State> {
    /** 顶栏「辅助模式」：开启后在各设置项下展示 Figma 说明（node 1861:53430） */
    static contextType = RbpCloudSubFunctionAuxiliaryModeContext;
    /** 由 `contextType` 注入 */
    context!: React.ContextType<typeof RbpCloudSubFunctionAuxiliaryModeContext>;

    /** 防止保存请求重叠（等同 RBP2 的 loading 锁） */
    private saveLocked = false;

    public constructor(props: Props) {
        super(props);
        this.state = {
            isSaving: false,
            menuData: [],
            curSelectIds: [],
            settingArr: buildInitialSettingRows(),
            initialPageDataReady: false,
        };
    }

    public async componentDidMount(): Promise<void> {
        await this.loadPageData();
    }

    /**
     * 拉取一级菜单与各项设置初值，失败时 Toast 提示。
     */
    private loadPageData = async (): Promise<void> => {
        /** 始终以完整模板为合并基底，避免仅重复执行 `componentDidMount` 时 state 结构不完整 */
        const baseRows = buildInitialSettingRows();
        try {
            const menuRes = await apiManage_rbp.get_menus_v3({ with_dish: false });
            let menuData: FirstMenu[] = [];
            if (_.get(menuRes, 'error')) {
                if (_.get(menuRes, 'message') !== 'canceled') {
                    GlobalNotification.error(i18n.t('kwa_setting_page_get_first_level_menu_failed'));
                }
            } else {
                menuData = menuRes as FirstMenu[];
            }
            const api41 = apiManage_rbp.getSettingById(41);
            const api17 = apiManage_rbp.getSettingById(17);
            const api16 = apiManage_rbp.getSettingById(16);
            const api44 = apiManage_rbp.getSettingById(44);
            const api61 = apiManage_rbp.getSettingById(61);
            const api76 = apiManage_rbp.getDishWeigthForKWA();
            const api104 = apiManage_rbp.getSettingById(104);
            const api127 = apiManage_rbp.getSettingById(127);
            const api128 = apiManage_rbp.getSettingById(128);
            const values = await Promise.all([api41, api17, api16, api44, api61, api76, api104, api127, api128]);
            let afterSelectIds: number[] = [];
            const afterSettingArr = baseRows.map((row) => {
                const next = { ...row };
                for (const res of values) {
                    const resId = _.get(res, '[0].id');
                    if (resId === 41) {
                        afterSelectIds = _.get(res, '[0].value.menu', []) as number[];
                    } else if (resId === next.key) {
                        let val: boolean | string | number = false;
                        switch (next.key) {
                            case 61:
                                val = _.get(res, '[0].value.enable');
                                break;
                            case 76:
                            case 104:
                            case 128:
                                val = _.get(res, '[0].value');
                                break;
                            case 127:
                                val = !!_.get(res, '[0].value');
                                break;
                            default:
                                val = _.get(res, '[0].value.status');
                        }
                        next.value = val;
                        break;
                    }
                }
                return next;
            });
            this.setState({
                settingArr: afterSettingArr,
                curSelectIds: afterSelectIds || [],
                menuData: menuData || [],
            });
        } finally {
            this.setState({ initialPageDataReady: true });
        }
    };

    /**
     * 按设置 id 组装 PATCH / POST 请求体，与 RBP2 `setParamById` 一致。
     * @param id - 设置 id
     * @returns 请求 body
     */
    private buildPatchBodyBySettingId(id: number): Record<string, unknown> {
        const row = this.state.settingArr.find((r) => r.key === id);
        if (!row) {
            return {};
        }
        const { value } = row;
        switch (id) {
            case 61:
                return { enable: value };
            case 76:
                return { value };
            case 104:
            case 127:
            case 128:
                return { stringValue: value };
            default:
                return { status: value };
        }
    }

    /**
     * 并行保存所有 KWA 相关设置；任一路由返回 error 时提示失败。
     */
    private persistSettings = async (): Promise<void> => {
        const { curSelectIds, settingArr } = this.state;
        const v128 = settingArr.find((r) => r.key === 128)?.value ?? 0;
        if (Number(v128) < 10) {
            GlobalNotification.error(i18n.t('kwa_setting_page_ovetime_setting_error'));
            return;
        }
        setLoading(true);
        const api41 = apiManage_rbp.patchSettingById(41, { menu: curSelectIds });
        const api17 = apiManage_rbp.patchSettingById(17, this.buildPatchBodyBySettingId(17));
        const api16 = apiManage_rbp.patchSettingById(16, this.buildPatchBodyBySettingId(16));
        const api44 = apiManage_rbp.patchSettingById(44, this.buildPatchBodyBySettingId(44));
        const api61 = apiManage_rbp.patchSettingById(61, this.buildPatchBodyBySettingId(61));
        const api76 = apiManage_rbp.postDishWeigthForKWA(this.buildPatchBodyBySettingId(76));
        const api104 = apiManage_rbp.patchSettingById(104, this.buildPatchBodyBySettingId(104));
        const api127 = apiManage_rbp.patchSettingById(127, this.buildPatchBodyBySettingId(127));
        const api128 = apiManage_rbp.patchSettingById(128, this.buildPatchBodyBySettingId(128));
        const results = await Promise.all([api41, api17, api16, api44, api61, api76, api104, api127, api128]);
        const failed = results.some((res) => _.get(res, 'error'));
        if (failed) {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } else {
            GlobalNotification.success(i18n.t('global_text_operate_success'));
        }
        setLoading(false);
    };

    /**
     * 点击保存：带锁避免重复提交。
     */
    private onClickSave = async (): Promise<void> => {
        if (this.saveLocked || this.state.isSaving) {
            return;
        }
        this.saveLocked = true;
        this.setState({ isSaving: true });
        try {
            await this.persistSettings();
        } finally {
            this.saveLocked = false;
            this.setState({ isSaving: false });
        }
    };

    /**
     * 从已选列表移除一级菜单（Tag 上关闭操作）。
     * @param id - 一级菜单 `main_group_id`
     */
    private removeExcludedMenu = (id: number): void => {
        this.setState({ curSelectIds: this.state.curSelectIds.filter((x) => x !== id) });
    };

    /**
     * 从「新增菜单」下拉里追加一级菜单，下拉中已过滤已选项。
     * @param id - 一级菜单 `main_group_id`
     */
    private addExcludedMenu = (id: number): void => {
        const { curSelectIds } = this.state;
        if (curSelectIds.includes(id)) {
            return;
        }
        this.setState({ curSelectIds: [...curSelectIds, id] });
    };

    /**
     * 修改开关或输入项的值；含 104 的 3–20 与数字非负等校验，与 RBP2 一致。
     * @param val - 新值
     * @param key - 设置 id
     */
    private onChangeSetting = (val: boolean | string | number, key: number): void => {
        const { settingArr } = this.state;
        const after = settingArr.map((row) => {
            if (row.key !== key) {
                return row;
            }
            let nextVal: boolean | string | number = val;
            if (row.key === 104) {
                nextVal = Number(nextVal).toFixed(0);
                if (Number(nextVal) < 3) {
                    nextVal = '3';
                }
                if (Number(nextVal) > 20) {
                    nextVal = '20';
                }
            } else if (row.key === 127) {
                nextVal = Number(nextVal);
            } else if (row.key === 128) {
                nextVal = Number(nextVal).toFixed(0);
            }
            if (row.type === 'input' && row.inputType === 'number' && Number(nextVal) <= 0) {
                nextVal = '0';
            }
            return { ...row, value: nextVal };
        });
        this.setState({ settingArr: after });
    };

    /**
     * 按 id 列表从 `settingArr` 取出用于渲染的行。
     */
    private rowsByKeys(keys: readonly number[]): KwaSettingRow[] {
        return keys.map((k) => this.state.settingArr.find((r) => r.key === k)).filter(Boolean) as KwaSettingRow[];
    }

    public render(): React.ReactNode {
        if (!this.state.initialPageDataReady) {
            return <KwaSettingPageSkeleton />;
        }
        const { menuData, curSelectIds, isSaving } = this.state;
        const coreRows = this.rowsByKeys(KWA_CORE_SETTING_KEYS);
        const runtimeRows = this.rowsByKeys(KWA_RUNTIME_SETTING_KEYS);
        return (
            <Box className="kwa-setting-page">
                {/* 与云 RBP 子功能内容区一致：灰底圆角 16 + 投影，页头与内容同层（对齐 Figma 1861:53430） */}
                <Box className="kwa-setting-page__surface">
                    <Box className="kwa-setting-page__header">
                        <Typography variant="h4" color="primary" className="kwa-setting-page__page-title">
                            {i18n.t('kwa_setting_page_heading')}
                        </Typography>
                        <SubFunctionProjectSettingSaveButton loading={isSaving} onClick={() => void this.onClickSave()} />
                    </Box>

                    <Box className="kwa-setting-page__menu-card">
                        <Box className="kwa-setting-page__menu-head">
                            <Typography variant="subtitle1" className="kwa-setting-page__block-title">
                                {i18n.t('kwa_setting_page_not_joinin_round_advance_menu')}
                            </Typography>
                            <Typography variant="subtitle1" className="kwa-setting-page__block-title">
                                {i18n.t('kwa_setting_page_added_count', { count: curSelectIds.length })}
                            </Typography>
                        </Box>
                        <Typography variant="body2" className="kwa-setting-page__text-secondary">
                            {i18n.t(
                                this.context?.auxiliaryModeEnabled
                                    ? 'figma_aux_kwa_menu_round_block'
                                    : 'kwa_setting_page_menu_hint',
                            )}
                        </Typography>
                        <FirstLevelMenuTagPicker
                            selectedIds={curSelectIds}
                            allMenus={menuData}
                            onAdd={this.addExcludedMenu}
                            onRemove={this.removeExcludedMenu}
                            addButtonI18nKey="kwa_setting_page_add_menu"
                            removeChipAriaI18nKey="kwa_setting_page_menu_tag_remove_aria"
                            listAriaLabelI18nKey="kwa_setting_page_not_joinin_round_advance_menu"
                            borderedContainer={false}
                        />
                    </Box>

                    <Grid container spacing={3} className="kwa-setting-page__columns">
                        <Grid item xs={12} md={6}>
                            <Box className="kwa-setting-page__column">
                                <Typography variant="subtitle1" className="kwa-setting-page__column-title kwa-setting-page__block-title">
                                    {i18n.t('kwa_setting_page_core_settings')}
                                </Typography>
                                <Stack spacing={3}>
                                    {coreRows.map((row) => (
                                        <Box key={row.key} className="kwa-setting-page__row">
                                            <Box className="kwa-setting-page__row-label">
                                                <Stack spacing={0.5}>
                                                    <Typography variant="body1" className="kwa-setting-page__row-label-text">
                                                        {i18n.t(row.labelKey)}
                                                    </Typography>
                                                    <SubFunctionAuxiliaryHint
                                                        enabled={!!this.context?.auxiliaryModeEnabled}
                                                        i18nKey={KWA_AUX_HINT_I18N_KEYS[row.key] ?? 'figma_aux_sold_out_disabled_in_menu'}
                                                        className="kwa-setting-page__aux-hint"
                                                    />
                                                </Stack>
                                            </Box>
                                            <Box className="kwa-setting-page__row-control">
                                                <IOSSwitch
                                                    checked={!!row.value}
                                                    onChange={() => this.onChangeSetting(!row.value, row.key)}
                                                    inputProps={{ 'aria-label': i18n.t(row.labelKey) }}
                                                />
                                            </Box>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box className="kwa-setting-page__column">
                                <Typography variant="subtitle1" className="kwa-setting-page__column-title kwa-setting-page__block-title">
                                    {i18n.t('kwa_setting_page_runtime_settings')}
                                </Typography>
                                <Stack spacing={3}>
                                    {runtimeRows.map((row) => (
                                        <Box key={row.key} className="kwa-setting-page__row">
                                            <Box className="kwa-setting-page__row-label">
                                                <Stack spacing={0.5}>
                                                    <Typography variant="body1" className="kwa-setting-page__row-label-text">
                                                        {i18n.t(row.labelKey)}
                                                    </Typography>
                                                    <SubFunctionAuxiliaryHint
                                                        enabled={!!this.context?.auxiliaryModeEnabled}
                                                        i18nKey={KWA_AUX_HINT_I18N_KEYS[row.key] ?? 'figma_aux_sold_out_disabled_in_menu'}
                                                        className="kwa-setting-page__aux-hint"
                                                    />
                                                </Stack>
                                            </Box>
                                            <Box className="kwa-setting-page__row-control">
                                                {row.type === 'switch' ? (
                                                    <IOSSwitch
                                                        checked={!!row.value}
                                                        onChange={() => this.onChangeSetting(!row.value, row.key)}
                                                        inputProps={{ 'aria-label': i18n.t(row.labelKey) }}
                                                    />
                                                ) : (
                                                    <CommonInput
                                                        className="kwa-setting-page__number-field"
                                                        inputType="INPUT"
                                                        type={row.inputType}
                                                        size="small"
                                                        value={String(row.value ?? '')}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                            this.onChangeSetting(e.target.value, row.key)
                                                        }
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* 底部提示：对齐 Figma Frame 427320251 — 横向居中、左侧图标 40；文案区为纵向两行，行距 16 */}
                    <Box className="kwa-setting-page__bottom-tip">
                        <ErrorRoundedIcon className="kwa-setting-page__tip-icon" aria-hidden />
                        <Stack className="kwa-setting-page__tip-text" spacing={4}>
                            <Typography variant="subtitle1" component="p" className="kwa-setting-page__tip-lead">
                                {i18n.t('kwa_setting_page_tip_prefix')}
                            </Typography>
                            <Typography variant="subtitle2" component="p" className="kwa-setting-page__tip-body">
                                {i18n.t('kwa_setting_page_tip_body')}
                            </Typography>
                        </Stack>
                    </Box>
                </Box>
            </Box>
        );
    }
}

export default KwaSettingPage;
